import type { ShapeData } from '@/lib/shapes/types/shape-data'
import type { TextChain } from '@/api/types/stowed-element'
import { isShapeTextFrame } from '@/lib/shapes/shape-text-frame'
import { isLinkedTextFrame } from '@/lib/groups/linked-text-group-policy'
import { DEFAULT_POINT_SIZE } from '@/lib/text/constants/text-defaults'

/**
 * Legacy-only text representation (`<PE><XX>` text-on-a-path); nothing in current code creates
 * this kindId, but a spread loaded from legacy can still carry one. Text-to-shape excludes it —
 * matching legacy's own `isSelectionHasTOP()` gate on the same menu item.
 */
const TEXT_ON_PATH_KIND_ID = 6

/** Minimum point size, across every run in the text, required for text-to-shape (legacy parity:
 *  `minFontSize < 24` blocks conversion in `odctrl.js`'s `validateTextToShape`). */
export const TEXT_TO_SHAPE_MIN_FONT_SIZE_PT = 24

/** Maximum non-whitespace character count allowed for text-to-shape (legacy parity:
 *  `maxCharacters > 50` blocks conversion). */
export const TEXT_TO_SHAPE_MAX_CHARACTERS = 50

export const TEXT_TO_SHAPE_INELIGIBLE_MESSAGE =
  'Text cannot be converted if it is smaller than 24 pt or exceeds 50 characters.'

/** Shown when Convert Text to Shape is invoked with no eligible text frame selected at all. */
export const TEXT_TO_SHAPE_NO_SELECTION_MESSAGE = 'Select a text frame to convert it to a shape.'

/**
 * True when a shape is structurally excluded from text-to-shape regardless of size/length —
 * locked, frozen, linked into a text chain, legacy text-on-a-path, or not a text frame at all.
 * Drives menu/context-menu disabling (checked per-candidate, before the user ever clicks).
 */
export function isTextToShapeStructurallyBlocked(
  shape: ShapeData,
  textChains: readonly TextChain[] | undefined
): boolean {
  if (!isShapeTextFrame(shape)) return true
  if (shape.locked === true) return true
  if (shape.frozen === true) return true
  if (shape.kindId === TEXT_ON_PATH_KIND_ID) return true

  return isLinkedTextFrame(shape, textChains)
}

/**
 * Smallest `pointSize` across every run in the shape's text, falling back to the frame's default
 * when a run omits it (matches how the rest of the shape-text engine treats a missing run size —
 * see `DEFAULT_POINT_SIZE` in `text-types/constants/text-defaults.ts`). Returns `DEFAULT_POINT_SIZE` for empty text so an
 * empty frame is never spuriously blocked by the size rule (the character-count rule already
 * passes it trivially at 0).
 */
export function computeShapeMinFontSizePt(shape: ShapeData): number {
  let min = Number.POSITIVE_INFINITY

  for (const paragraph of shape.paragraphs ?? []) {
    for (const line of paragraph.textLines ?? []) {
      for (const run of line.textRuns ?? []) {
        const size = run.pointSize ?? DEFAULT_POINT_SIZE

        if (size < min) min = size
      }
    }
  }

  return Number.isFinite(min) ? min : DEFAULT_POINT_SIZE
}

/**
 * Total character count across the shape's text, excluding whitespace-only runs — legacy parity:
 * `TextUtil.isPrintableString` (`odeditorsvg.js`) strips `[\r\n\t\s]+` and skips a run entirely
 * when nothing survives, so an inter-word space contributes 0 while a run that mixes real
 * characters with embedded whitespace still counts in full. Larry has no equivalent per-run
 * tokenization to reproduce that exactly, so this counts non-whitespace characters directly —
 * matching legacy's actual behavior for the overwhelming common case (plain inter-word spaces).
 */
export function countNonWhitespaceShapeTextCharacters(shape: ShapeData): number {
  let count = 0

  for (const paragraph of shape.paragraphs ?? []) {
    for (const line of paragraph.textLines ?? []) {
      for (const run of line.textRuns ?? []) {
        const stripped = (run.textContent ?? '').replace(/[\r\n\t\s]/g, '')

        count += stripped.length
      }
    }
  }

  return count
}

/**
 * The 24pt-minimum / 50-character-maximum rule — checked at conversion time (not menu-disable
 * time, per the acceptance criteria: a toast on failure, not a disabled menu item). Legacy parity:
 * `odctrl.js`'s `validateTextToShape`/`doTextToShape` (`minFontSize >= 24 && maxCharacters <= 50`).
 */
export function meetsTextToShapeSizeAndLengthRule(shape: ShapeData): boolean {
  return (
    computeShapeMinFontSizePt(shape) >= TEXT_TO_SHAPE_MIN_FONT_SIZE_PT &&
    countNonWhitespaceShapeTextCharacters(shape) <= TEXT_TO_SHAPE_MAX_CHARACTERS
  )
}
